import StartLocalConsultationDialogComponent from '../components/StartLocalConsultationDialog'
import { CreatePatientDto } from '@/core/models/patient.model'
import { useAppDispatch, useAppSelector } from '@/redux/rootReducer'
import {
  clearNewlyCreatedPatientId,
  createPatient,
  getPatients,
  selectNewlyCreatedPatientId,
  selectPatientListRequestTracker,
  selectPatients,
  selectPatientSearchResult,
} from '@/redux/reducers/patient.reducer'
import { useCallback, useEffect, useMemo } from 'react'
import { useDebouncedCallback } from 'use-debounce'
import { useRequestHandler } from '@/hooks/useRequestHandler'
import { useSnackbar } from 'notistack'
import { createConsultation, selectConsultationCreateRequestTracker, selectNewlyCreatedConsultationId } from '@/redux/reducers/consultation.reducer'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { ConsultationStatus, ConsultationType } from '@/core/models/consultation.model'
import { DeviceType } from './SelectDevice'

type StartLocalConsultationDialogProps = {
  open: boolean
  onClose: () => void
  type?: DeviceType
}
export const patientsPerPageOptions = [5, 10]

export default function StartLocalConsultationDialog(props: StartLocalConsultationDialogProps) {
  const { open, onClose, type } = props
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()

  const patients = useAppSelector(selectPatients)
  const patientSearchResult = useAppSelector(selectPatientSearchResult)
  const patientListRequestTracker = useAppSelector(selectPatientListRequestTracker)
  const newlyCreatedPatientId = useAppSelector(selectNewlyCreatedPatientId)
  const newlyCreatedConsultationId = useAppSelector(selectNewlyCreatedConsultationId)

  const patientsData = useMemo(
    () =>
      patients?.map((patient) => ({
        _id: patient._id,
        name: patient.userInfo.name,
        surname: patient.userInfo.surname,
        gender: patient.userInfo.gender,
        birthday: patient.userInfo.birthday,
        city: patient.contactInfo?.city,
        secu: patient.secu,
      })),
    [patients]
  )

  const startLocalConsultationRequestHandler = useRequestHandler({
    onSuccess: () => {
      enqueueSnackbar(t('notification.localConsultation.create.success'), { variant: 'success' })
      if (newlyCreatedConsultationId) {
        navigate(`/consultations/${newlyCreatedConsultationId}/local?${type !== undefined ? 'defaultTab=' + type : ''}`)
      }
    },
    onError: () => enqueueSnackbar(t('notification.consultation.create.error'), { variant: 'error' }),
    requestTrackerSelector: selectConsultationCreateRequestTracker,
  })

  const handleChangePage = useCallback(
    (newPage: number, rowsPerPage: number) => {
      dispatch(getPatients({ limit: rowsPerPage, skip: (newPage - 1) * rowsPerPage }))
    },
    [dispatch]
  )

  const handleCreatePatient = () => {
    const createPatientDto: CreatePatientDto = {
      agreement: 'Emergency',
      userInfo: {
        birthday: new Date().toISOString(),
        gender: 'I',
        name: new Date().toLocaleString().replace(/[/+:,\s]+/g, ''),
        surname: 'Urgence',
      },
      active: false,
      secu: Math.random().toString().substring(2, 17), // secu is required
    }
    dispatch(createPatient(createPatientDto))
  }

  const handleSearchPatient = useDebouncedCallback((textSearch: string) => {
    dispatch(getPatients({ smartSearch: textSearch || undefined, limit: patientsPerPageOptions[0] }))
  }, 300)

  useEffect(() => {
    if (newlyCreatedPatientId) {
      // create new local consultation and redirect
      startLocalConsultationRequestHandler.dispatch(
        createConsultation({
          consultation: {
            patient: newlyCreatedPatientId,
            type: ConsultationType.Local,
            status: ConsultationStatus.Active,
            startDate: new Date().toISOString(),
          },
        })
      )
    }
    return () => {
      dispatch(clearNewlyCreatedPatientId())
    }
  }, [dispatch, newlyCreatedPatientId, startLocalConsultationRequestHandler])

  const handleSelect = (patientId: string) => {
    // create new local consultation and redirect
    startLocalConsultationRequestHandler.dispatch(
      createConsultation({
        consultation: {
          patient: patientId,
          type: ConsultationType.Local,
          status: ConsultationStatus.Active,
          startDate: new Date().toISOString(),
        },
      })
    )
  }

  return (
    <StartLocalConsultationDialogComponent
      patients={patientsData}
      totalSearchCount={patientSearchResult?.total}
      loading={patientListRequestTracker.loading}
      onPageChange={handleChangePage}
      onPatientCreation={handleCreatePatient}
      onSearchPatient={handleSearchPatient}
      open={open}
      onClose={onClose}
      onSelect={handleSelect}
    />
  )
}
